import {UserActivityCategoryEnum} from '@mobi-components/global-solution';
import {
  Trip,
  TripOptionsRecommendation,
  TripOptionsRecommendationCategory,
  TripOptionsRecommendationPOI,
} from '@/store/api/download-destination-trips';
import {FilterTag} from '@/views/amex/app-types';

export interface DestinationDetailsCardInfo {
  id: string;
  title: string;
  subTitle?: string;
  images: string[];
  rating?: number;
  matchPercent?: number;
  priceLabel?: string;
  price?: string;
  tags?: string[];
  detailsRoute?: string;
  selectRoute?: string;
}

export const filterForUnique = <T>(value: T, index: number, array: T[]): boolean => {
  return array.lastIndexOf(value) === index;
};

export const getUniqueRecommendations = (recommendations: DestinationDetailsCardInfo[]) => {
  const uniqueRecommendations: DestinationDetailsCardInfo[] = [];
  for (let i = 0; i < recommendations.length; i++) {
    const nextReccomendation = recommendations[i];
    if (uniqueRecommendations.find((rec) => rec.title === nextReccomendation.title)) {
      continue;
    }
    uniqueRecommendations.push(nextReccomendation);
  }
  return uniqueRecommendations;
};

export const filterByPrimaryCategory = (
  recommendations: TripOptionsRecommendation[],
  category: TripOptionsRecommendationCategory,
): TripOptionsRecommendation | undefined => {
  return recommendations.find((item) => item.primary_category === category);
};

export const getFlightClass = (poi: TripOptionsRecommendationPOI) =>
  poi?.transportation?.segments?.[0]?.cabin_class?.toLowerCase() || 'Economy';

export const getFlightOperators = (poi: TripOptionsRecommendationPOI) => poi?.types?.join(' & ') || '';

export const RoutesBuilder = {
  summaryRoute(trip: Trip) {
    return trip ? `/summary/${trip.trip_id}/${trip.context.name}` : '';
  },

  exploreCategoryRoute(trip: Trip, category: UserActivityCategoryEnum) {
    return trip ? `/explore/${trip.trip_id}/${trip.context.name}/${category}` : '';
  },

  detailsRoute(trip: Trip, itemId: string) {
    return trip ? `/explore/${trip.trip_id}/${trip.context.name}/details/${itemId}` : '';
  },

  exploreAllRoute(primaryCategory: string, tripId: string, contextName: string) {
    return `/explore/${tripId}/${contextName}/${primaryCategory}`;
  },
};

export const flightCardBuilder = (
  card: DestinationDetailsCardInfo,
  poi: TripOptionsRecommendationPOI,
  trip: Trip,
): DestinationDetailsCardInfo => {
  return {
    ...card,
    // build title from flight class + flight operator name
    title: `${getFlightClass(poi)} Class, ${getFlightOperators(poi)}`,
    // subtilte
    subTitle: 'Flights',
    // no rating for flights
    rating: undefined,
    // flights price label
    priceLabel: 'Starting at',
    // detailsRoute => to the Explore Flights Page
    detailsRoute: RoutesBuilder.exploreCategoryRoute(trip, UserActivityCategoryEnum.FLIGHT),
    // selectRoute => to the Trip Summary Page with the chosen item pre-selected. TODO: how to pre-select item at summary page?
    selectRoute: RoutesBuilder.summaryRoute(trip),
  };
};

export const accommodationCardBuilder = (
  card: DestinationDetailsCardInfo,
  poi: TripOptionsRecommendationPOI,
  trip: Trip,
): DestinationDetailsCardInfo => {
  return {
    ...card,
    // hotels price label
    priceLabel: 'Per Night',
    // detailsRoute => to the Explore Stays Page
    detailsRoute: RoutesBuilder.detailsRoute(trip, poi.id),
    // selectRoute => to the Trip Summary Page with the chosen item pre-selected. TODO: how to pre-select item at summary page?
    selectRoute: RoutesBuilder.summaryRoute(trip),
  };
};

export const diningCardBuilder = (
  card: DestinationDetailsCardInfo,
  poi: TripOptionsRecommendationPOI,
  trip: Trip,
): DestinationDetailsCardInfo => {
  return {
    ...card,
    // dinings price label
    priceLabel: 'Average Bill Per Person',
    // detailsRoute => to the Explore Dinings page
    detailsRoute: RoutesBuilder.detailsRoute(trip, poi.id),
    // selectRoute => to the Trip Summary Page with the chosen item pre-selected. TODO: how to pre-select item at summary page?
    selectRoute: RoutesBuilder.summaryRoute(trip),
  };
};

export const experienceCardBuilder = (
  card: DestinationDetailsCardInfo,
  poi: TripOptionsRecommendationPOI,
  trip: Trip,
): DestinationDetailsCardInfo => {
  return {
    ...card,
    // experiences price label
    priceLabel: undefined,
    // detailsRoute => to the Explore Experiences page
    detailsRoute: RoutesBuilder.detailsRoute(trip, poi.id),
    // selectRoute => to the Trip Summary Page with the chosen item pre-selected. TODO: how to pre-select item at summary page?
    selectRoute: RoutesBuilder.summaryRoute(trip),
  };
};

export const cardBuilder = (
  poi: TripOptionsRecommendationPOI,
  category: TripOptionsRecommendationCategory,
  trip: Trip,
): DestinationDetailsCardInfo => {
  const categoryBuilders = {
    [UserActivityCategoryEnum.FLIGHT]: flightCardBuilder,
    [UserActivityCategoryEnum.ACCOMMODATION]: accommodationCardBuilder,
    [UserActivityCategoryEnum.DINING]: diningCardBuilder,
    [UserActivityCategoryEnum.EXPERIENCE]: experienceCardBuilder,
  };

  const card = {
    id: poi.id,
    title: poi.name,
    images: [poi.images.medium],
    rating: poi.rating,
    priceLabel: 'Per Night',
    price: poi.prices?.[0] ? `${poi.prices[0].currency} ${Math.ceil(poi.prices[0].amount)}` : '',
    tags: poi.tags.filter(filterForUnique),
  };

  return categoryBuilders[category]?.(card, poi, trip) || card;
};

export const buildCardsfromRecommendation = (recommendations: TripOptionsRecommendation, trip: Trip) => {
  return recommendations
    ? recommendations.pois.map((item) =>
        cardBuilder(item, recommendations.primary_category as TripOptionsRecommendationCategory, trip),
      )
    : [];
};

export const EMPTY_RECOMMENDATIONS = [
  {
    primary_category: UserActivityCategoryEnum.FLIGHT,
    pois: [],
    theme: '',
    isLoading: true,
  },
  {
    primary_category: UserActivityCategoryEnum.ACCOMMODATION,
    pois: [],
    theme: '',
    isLoading: true,
  },
  {
    primary_category: UserActivityCategoryEnum.DINING,
    pois: [],
    theme: '',
    isLoading: true,
  },
  {
    primary_category: UserActivityCategoryEnum.EXPERIENCE,
    pois: [],
    theme: '',
    isLoading: true,
  },
];

// mark tag as disabled if it is not matched in any of the cards. Put disabled tags at the end of the list.
export const disableAndSortNotMatchedTags = (tags: FilterTag[], cards: DestinationDetailsCardInfo[]): FilterTag[] => {
  const tagsList = tags.map((tag) => {
    let tagMatchedInCards = false;

    for (let i = 0; i < cards.length; i++) {
      if (cards[i].tags && (cards[i].tags as string[]).indexOf(tag.name) >= 0) {
        tagMatchedInCards = true;
        break;
      }
    }

    return {...tag, disabled: !tagMatchedInCards};
  });

  const enabledTags = tagsList.filter((tag) => !tag.disabled);
  const disabledTags = tagsList.filter((tag) => tag.disabled);

  return [...enabledTags, ...disabledTags];
};

export const filterCardsByOneTag = (
  cards: DestinationDetailsCardInfo[],
  tag: FilterTag,
): DestinationDetailsCardInfo[] => {
  return cards.filter((card) => {
    return card.tags && card.tags?.indexOf(tag.name) >= 0;
  });
};

export const filterCardsByAnyOfTags = (
  cards: DestinationDetailsCardInfo[],
  selectedTags: FilterTag[],
): DestinationDetailsCardInfo[] => {
  return cards.filter((card) => {
    // if card has no tags - do not include it
    if (!card.tags || card.tags.length === 0) {
      return false;
    }

    // tagFound will become true if any of tag names is present in card.tags
    let tagFound = false;
    // using every instead of forEach, to be able to break loop
    selectedTags.every((oneOfTags) => {
      if ((card.tags as string[]).includes(oneOfTags.name)) {
        // found tag, exit the loop
        tagFound = true;
        return false;
      }
      // continue loop
      return true;
    });

    return tagFound;
  });
};

export const filterCardsByAllTags = (
  cards: DestinationDetailsCardInfo[],
  selectedTags: FilterTag[],
): DestinationDetailsCardInfo[] => {
  const filteredCards = cards.filter((card) => {
    // if card has no tags - do not include it
    if (!card.tags || card.tags.length === 0) {
      return false;
    }

    // tagFound will remain true only if every one of tag names is present in card.tags
    let tagFound = true;
    // using every instead of forEach, to be able to break loop
    selectedTags.every((oneOfTags) => {
      if (!(card.tags as string[]).includes(oneOfTags.name)) {
        // one tag not found, exit the loop
        tagFound = false;
        // exit the loop
        return false;
      }
      // continue loop
      return true;
    });

    return tagFound;
  });

  return filteredCards;
};

export enum FILTER_MODE {
  BY_ANY_OF_TAGS = 'any',
  BY_ALL_OF_TAGS = 'all',
}

export const FilterCardsFunctionsByMode = {
  [FILTER_MODE.BY_ANY_OF_TAGS]: filterCardsByAnyOfTags,
  [FILTER_MODE.BY_ALL_OF_TAGS]: filterCardsByAllTags,
};

const defaultFilterCardsFunctions = filterCardsByAnyOfTags;
export const getFilterCardsFunction = (mode: FILTER_MODE) =>
  FilterCardsFunctionsByMode[mode] || defaultFilterCardsFunctions;

export const calcCardMatchedPercent = (card: DestinationDetailsCardInfo, selectedTags: FilterTag[]): number => {
  const cardTags = card.tags || [];
  const selectedTagsNames = selectedTags.map((tag) => tag.name);

  // if no tags selected - return 100% match
  if (!selectedTagsNames.length) {
    return 100;
  }

  const matchedTags = cardTags.filter((tag) => selectedTagsNames.includes(tag));
  const matchedPercent = Math.round((matchedTags.length / selectedTagsNames.length) * 100);

  return matchedPercent;
};
